import { ComponentPropsWithoutRef, createContext, forwardRef, useContext, useId } from 'react';

import { Typography } from '@braskai/ui-kit';
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext } from 'react-hook-form';

import { FormControl as MuiFormControl, Stack } from '@mui/material';

import { SInputLabel } from './styled';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Stack>>(({ ...props }, ref) => {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <Stack gap={6} ref={ref} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { formState, getFieldState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formDescriptionId: `${id}-form-item-description`,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormLabel = forwardRef<HTMLLabelElement, ComponentPropsWithoutRef<typeof SInputLabel>>(
  ({ children, ...props }, ref) => {
    const { formItemId } = useFormField();

    return (
      <SInputLabel htmlFor={formItemId} ref={ref} {...props}>
        {children}
      </SInputLabel>
    );
  },
);
FormLabel.displayName = 'FormLabel';

const FormControl = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MuiFormControl>>(
  ({ children, ...props }, ref) => {
    const { error } = useFormField();

    return (
      <MuiFormControl error={!!error} ref={ref} fullWidth {...props}>
        {children}
      </MuiFormControl>
    );
  },
);
FormControl.displayName = 'FormControl';

const FormDescription = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<typeof Typography>>(
  ({ ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return <Typography id={formDescriptionId} color="grey.500" component="p" ref={ref} variant="body-2" {...props} />;
  },
);
FormDescription.displayName = 'FormDescription';

const FormMessage = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<typeof Typography>>(
  ({ children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <Typography id={formMessageId} color="error.500" component="p" ref={ref} variant="body-2" {...props}>
        {body}
      </Typography>
    );
  },
);
FormMessage.displayName = 'FormMessage';

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
